import React from 'react';

import styled, { css } from 'styled-components';

import useLocalizedPath from '~/shared/utils/useLocalizedPath';
import { Link } from '~/techstyle-shared/next-routes';
import {
  desktop,
  mobile,
  Separator,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import { fabrics } from '../../utils/fabricTypeGuideConstants';

const FabricGuide = styled.div``;

const FabricGuideWrapper = styled.div`
  ${desktop`
    display: flex;
    flex-wrap: wrap;
    width: 900px;
  `}
`;

const FabricGuideHeader = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.fabricGuideHeader};
  text-align: center;
  margin: 24px auto 32px;
`;

const FabricContainer = styled.div`
  display: flex;
  margin-bottom: 16px;

  ${desktop`
    text-align: center;
    margin-right: 24px;
    margin-bottom: 32px;
  `}
`;

const MobileFabricContent = styled.div`
  margin-right: 16px;

  ${desktop`
    display: none;
  `}
`;

const DesktopFabricContent = styled.div`
  ${mobile`
    display: none;
  `}
`;

const FabricImage = styled.img`
  width: 266px;
  height: 200px;

  ${mobile`
    width: 152px;
    height: 152px;
  `}
`;

const FabricContent = styled.div``;

const FabricTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: ${0.4 / 18}em;

  ${mobile`
    margin-top: 14px;
    margin-bottom: 10px;
  `}
`;

const FabricCompression = styled.div`
  color: ${({ theme }) => theme.colors.fadedBackground};
  text-transform: uppercase;
  font-size: 13px;
  line-height: ${16 / 13};
  letter-spacing: ${0.2 / 13}em;
  font-weight: 700;
  margin-bottom: 8px;
`;

const FabricCompressionRating = styled.div`
  color: ${({ theme }) => theme.colors.fadedBackground};
  margin-bottom: 16px;
`;

const CompressionFullGauge = styled.span`
  height: 8px;
  width: 8px;
  background-color: ${({ theme }) => theme.colors.fadedBackground};
  border-radius: 50%;
  display: inline-block;
  margin-right: 4px;
`;

const CompressionEmptyGauge = styled.span`
  height: 8px;
  width: 8px;
  border: 1px solid ${({ theme }) => theme.colors.fadedBackground};
  border-radius: 50%;
  display: inline-block;
  margin-right: 4px;
`;

const FabricDescription = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: ${16 / 14};
  font-weight: 400;

  ${desktop`
    font-size: 13px;
    line-height: ${16 / 13};
    margin-top: 16px;
  `}
`;

const FabricLink = styled.a`
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
  letter-spacing: ${0.4 / 14}em;
  text-decoration: underline;
`;

const separatorStyle = css`
  margin: 16px 0;

  ${mobile`
    display: none;
  `}
`;

const Line = () => {
  return (
    <Separator
      color={({ theme }) => theme.colors.womenFabricChartSeparator}
      thickness={1}
      separatorStyle={separatorStyle}
    />
  );
};

const DynamicFormattedMessage = FormattedMessage;

function WomenFabricChart() {
  const fabricGuidePath = useLocalizedPath('/womens/bottoms/leggings');
  const { tld } = useDomain();
  const isDK = tld === '.dk';
  const compressionRange = 5;

  return (
    <FabricGuide>
      <FabricGuideHeader>
        <FormattedMessage
          id="size_guide.compare_fabric"
          defaultMessage="Compare the fit and compression of our signature fabrics below."
        />
      </FabricGuideHeader>
      <FabricGuideWrapper>
        {fabrics.map((fabric) => (
          <FabricContainer key={fabric.name}>
            <MobileFabricContent>
              <FabricImage src={fabric.imgSrc} />
            </MobileFabricContent>
            <FabricContent>
              <FabricTitle>
                <DynamicFormattedMessage
                  id={`size_guide.${fabric.titleMessageId}`}
                  defaultMessage={fabric.titleDefaultMessage}
                />
              </FabricTitle>
              <Line />
              <FabricCompression>
                <DynamicFormattedMessage
                  id={`size_guide.${fabric.compressionMessageId}`}
                  defaultMessage={fabric.compressionDefaultMessage}
                />
              </FabricCompression>
              <FabricCompressionRating>
                {Array(fabric.compression).fill(<CompressionFullGauge />)}
                {Array(compressionRange - fabric.compression).fill(
                  <CompressionEmptyGauge />
                )}
              </FabricCompressionRating>
              <DesktopFabricContent>
                <FabricImage src={fabric.imgSrc} />
              </DesktopFabricContent>
              <FabricDescription>
                <DynamicFormattedMessage
                  id={`size_guide.${fabric.descriptionMessageId}`}
                  defaultMessage={fabric.descriptionDefaultMessage}
                />
              </FabricDescription>
              <Line />
              {isDK ? (
                <Link href={`/${fabric.dkName}`} passHref>
                  <FabricLink>
                    <FormattedMessage
                      id="size_guide.learn_more"
                      defaultMessage="Learn more"
                    />
                  </FabricLink>
                </Link>
              ) : (
                <Link
                  href={`${fabricGuidePath}?fabric=${fabric.name}`}
                  passHref
                >
                  <FabricLink>
                    <FormattedMessage
                      id="size_guide.learn_more"
                      defaultMessage="Learn more"
                    />
                  </FabricLink>
                </Link>
              )}
            </FabricContent>
          </FabricContainer>
        ))}
      </FabricGuideWrapper>
    </FabricGuide>
  );
}

export default WomenFabricChart;
